/**
 * Manage Investors — the governed meaning of each raw BankDwn investor code.
 *
 * Sibling of api/capital.ts, and deliberately the same shape: Manage Capital governs WHICH
 * codes constitute a capital source, this governs what those codes ARE. Same propose ->
 * approve -> effective-dated -> permanently-logged contract, so the hooks, the status vocabulary
 * and the review payload all read alike.
 *
 * Backend: portfolio-risk-concentrations/app/router/investor_listing.py, tables in
 * sql/106_prc_investor_code_listing.sql.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { QueryClient } from "@tanstack/react-query";

import { getJson, postJson, PRC_PREFIX } from "./client";

/**
 * Which fact a proposal changes.
 *
 * LISTING  the code's name and note. Creates the row when there is no live one, which is
 *          also how a retired code is brought back.
 * RETIRE   ends the live row and inserts no successor.
 *
 * There is no `is_active` anywhere: an inactive code is one with no LIVE row. See the router's
 * module header for why that is one predicate rather than two.
 */
export type InvestorListingChangeKind = "LISTING" | "RETIRE";

export const LISTING_KIND_LABEL: Record<InvestorListingChangeKind, string> = {
  LISTING: "Listing",
  RETIRE: "Retire",
};

/** Whether a code is Owned or Serviced. A governed attribute of the code: changed by a LISTING
 *  proposal, effective on approval. An existing code has none (null) until someone sets it. */
export const OWNERSHIP_VALUES = ["Owned", "Serviced"] as const;
export type Ownership = (typeof OWNERSHIP_VALUES)[number];

export interface InvestorListing {
  listingId: string;
  /** Canonical digit string ('20', never '20.0') — matches the Mapped-codes chips. */
  investorCode: string;
  codeMapping: string;
  notes: string | null;
  /**
   * Which live capital sources currently name this code, e.g. "Wafra · CAP1". Derived
   * server-side from prc_facility_investor_code_map; null when nothing maps it.
   */
  usedBy: string | null;
  /** Owned or Serviced; null until set. Edited here, through a proposal. */
  ownership: Ownership | null;
  /**
   * The facility code(s) Manage Capital maps this code to, e.g. "CAP1"; null when none (an Equity
   * mapping has no facility, and an unmapped code has nothing). Read-only on this page: a facility
   * mapping is edited in Manage Capital only.
   */
  facility: string | null;
  effectiveStartDate: string;
  effectiveEndDate: string | null;
  status: "LIVE" | "SUPERSEDED";
}

export interface InvestorListingProposal {
  proposalId: string;
  /** What was live when the proposal was RAISED. Null on a creation. */
  listingId: string | null;
  investorCode: string;
  changeKind: InvestorListingChangeKind;
  changeType: "ADD" | "ADJUST";
  oldCodeMapping: string | null;
  newCodeMapping: string | null;
  oldNotes: string | null;
  newNotes: string | null;
  oldOwnership: Ownership | null;
  newOwnership: Ownership | null;
  effectiveStartDate: string;
  proposedByRole: string;
  proposedAt: string;
  reason: string | null;
  status: "PENDING" | "APPROVED" | "REJECTED";
  reviewedByRole: string | null;
  reviewedAt: string | null;
  reviewNotes: string | null;
}

export interface InvestorListingProposalInput {
  investorCode: string;
  changeKind: InvestorListingChangeKind;
  /** Required for LISTING; omitted for RETIRE. */
  codeMapping?: string | null;
  notes?: string | null;
  /** Required when adding or reactivating a code; omit to leave an existing code's value as it is. */
  ownership?: Ownership | null;
  effectiveStartDate: string;
  reason: string;
}

export interface InvestorListingReviewResult {
  ok: boolean;
  error?: string;
  decision?: "APPROVED" | "REJECTED";
  applied?: boolean;
  changeKind?: InvestorListingChangeKind;
  investorCode?: string;
  codeMapping?: string | null;
}

/**
 * Both reads go to the real backend unconditionally.
 *
 * There is no PRC hook with a mock-data fallback any more (the old `isMock` layer, and the
 * bug it caused — a mocked read path masking a real, persisting write — are both gone). This
 * comment stays because this module was the first one built without that fallback, and the
 * reasoning ("a read path that lies while the write path persists is worse than either a real
 * backend or an honest mock") is why it was removed everywhere else too.
 */
export function useInvestorListings(
  includeSuperseded = false,
  page: { limit: number; offset: number } = { limit: 10, offset: 0 },
) {
  return useQuery({
    queryKey: ["prc-investor-listings", includeSuperseded, page],
    queryFn: () => {
      const q = new URLSearchParams({ limit: String(page.limit), offset: String(page.offset) });
      if (includeSuperseded) q.set("includeSuperseded", "true");
      return getJson<{ listings: InvestorListing[]; total: number }>(
        `${PRC_PREFIX}investor-listings?${q.toString()}`,
      );
    },
  });
}

export function useInvestorListingProposals(
  page: { limit: number; offset: number } = { limit: 10, offset: 0 },
) {
  return useQuery({
    queryKey: ["prc-investor-listing-proposals", page],
    queryFn: () => {
      const q = new URLSearchParams({ limit: String(page.limit), offset: String(page.offset) });
      return getJson<{ proposals: InvestorListingProposal[]; total: number }>(
        `${PRC_PREFIX}investor-listing-proposals?${q.toString()}`,
      );
    },
  });
}

/**
 * Narrower than invalidateCapital on purpose. A capital change moves the denominator under
 * every limit, so it invalidates capacity, concentration and joints too; a code's NAME is
 * descriptive only — nothing downstream computes off it.
 */
function invalidateInvestorListings(qc: QueryClient) {
  qc.invalidateQueries({ queryKey: ["prc-investor-listings"] });
  qc.invalidateQueries({ queryKey: ["prc-investor-listing-proposals"] });
}

export function useProposeInvestorListing() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: InvestorListingProposalInput) =>
      postJson<{ proposalId: string; changeType: "ADD" | "ADJUST" }>(
        `${PRC_PREFIX}investor-listing-proposals`,
        input,
      ),
    onSuccess: () => invalidateInvestorListings(qc),
  });
}

export function useReviewInvestorListing() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      proposalId: string;
      decision: "APPROVED" | "REJECTED";
      notes: string;
    }) =>
      postJson<InvestorListingReviewResult>(
        `${PRC_PREFIX}investor-listing-proposals/${encodeURIComponent(input.proposalId)}/review`,
        { decision: input.decision, notes: input.notes },
      ),
    onSuccess: () => invalidateInvestorListings(qc),
  });
}

/** The live listing for a code, or undefined. Used by the dialog to detect an adjustment. */
export function findLiveListing(
  listings: InvestorListing[],
  investorCode: string,
): InvestorListing | undefined {
  return listings.find((l) => l.investorCode === investorCode && l.status === "LIVE");
}

/**
 * How a proposal reads in the log's Change column.
 *
 * Renders each field that actually moved, so an edit that only touched the note does not
 * claim the name changed too. "—" stands in for an absent side, which is what an ADD looks
 * like: "— → NER 13-1".
 */
export function listingChangeSummary(p: InvestorListingProposal): string {
  if (p.changeKind === "RETIRE") {
    return `Retired${p.oldCodeMapping ? ` (${p.oldCodeMapping})` : ""}`;
  }
  const parts: string[] = [];
  if ((p.oldCodeMapping ?? null) !== (p.newCodeMapping ?? null)) {
    parts.push(`${p.oldCodeMapping || "—"} → ${p.newCodeMapping || "—"}`);
  }
  if ((p.oldNotes ?? null) !== (p.newNotes ?? null)) {
    parts.push(`note: ${p.oldNotes || "—"} → ${p.newNotes || "—"}`);
  }
  if ((p.oldOwnership ?? null) !== (p.newOwnership ?? null)) {
    parts.push(`ownership: ${p.oldOwnership || "—"} → ${p.newOwnership || "—"}`);
  }
  return parts.length ? parts.join(" · ") : "No change";
}
